"use client";

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '@/context/LanguageContext';
import AuthorityStripe from './AuthorityStripe';

export default function Header() {
    const { t } = useLanguage();
    const [isToolsOpen, setIsToolsOpen] = useState(false);
    const [isLegalOpen, setIsLegalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pathname = usePathname();

    const toolCategories = [
        {
            name: t('cat_text'),
            tools: [
                { name: t('sort_text_title'), href: '/tools/sort-text-online' },
                { name: t('find_replace_title'), href: '/tools/find-and-replace-text' },
                { name: t('word_counter_title'), href: '/tools/online-word-counter' },
                { name: t('diff_checker_title'), href: '/tools/text-difference-checker' },
                { name: t('duplicate_remover_title'), href: '/tools/remove-duplicate-text-lines' },
                { name: t('grammar_checker_title'), href: '/tools/grammar-checker' },
                { name: t('case_converter_title'), href: '/tools/text-formatter' },
                { name: t('html_stripper_title'), href: '/tools/strip-html-tags-online' },
                { name: t('emoji_remover_title'), href: '/tools/remove-emojis-from-text' },
                { name: t('prefix_suffix_title'), href: '/tools/add-prefix-suffix-to-lines' },
                { name: t('line_break_title'), href: '/tools/add-line-breaks-to-text' },
            ]
        },
        {
            name: t('cat_financial'),
            tools: [
                { name: t('gst_calc_title'), href: '/tools/gst-calculator-india' },
                { name: t('emi_calc_title'), href: '/tools/emi-calculator' },
                { name: t('tax_calc_title'), href: '/tools/income-tax-calculator' },
                { name: t('salary_calc_title'), href: '/tools/salary-calculator-india' },
                { name: t('loan_calc_title'), href: '/tools/loan-calculator' },
                { name: t('profit_calc_title'), href: '/tools/profit-margin-calculator' },
                { name: t('discount_calc_title'), href: '/tools/discount-calculator' },
                { name: t('perc_calc_title'), href: '/tools/percentage-calculator' },
                { name: t('invoice_gen_title'), href: '/tools/invoice-generator' },
                { name: t('quotation_gen_title'), href: '/tools/business-quotation-generator' },
                { name: t('expense_track_title'), href: '/tools/expense-tracker-tool' },
            ]
        },
        {
            name: t('cat_security'),
            tools: [
                { name: t('age_calc_title'), href: '/tools/age-calculator' },
                { name: t('duration_calc_title'), href: '/tools/time-duration-calculator' },
                { name: t('password_gen_title'), href: '/tools/password-generator' },
                { name: t('username_gen_title'), href: '/tools/username-generator' },
                { name: t('random_number_title'), href: '/tools/random-number-generator' },
                { name: t('random_string_title'), href: '/tools/random-string-generator' },
                { name: t('email_extractor_title'), href: '/tools/email-extractor-tool' },
                { name: t('url_extractor_title'), href: '/tools/url-extractor-tool' },
                { name: t('json_formatter_title'), href: '/tools/json-formatter' },
                { name: t('pdf_merge_title'), href: '/tools/merge-pdf' },
                { name: t('pdf_split_title'), href: '/tools/split-pdf' },
            ]
        }
    ];

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [pathname]);

    const handleMouseEnter = () => {
        if (window.innerWidth < 1024) return;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsToolsOpen(true);
    };

    const handleMouseLeave = () => {
        if (window.innerWidth < 1024) return;
        timeoutRef.current = setTimeout(() => {
            setIsToolsOpen(false);
        }, 300);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    return (
        <header className="google-auto-ads-no-ad" style={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderBottom: '1px solid var(--border)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
        }}>
            <style jsx global>{`
                @media (max-width: 768px) {
                    .cookie-inner {
                        flex-direction: column;
                        align-items: center;
                        gap: 0.75rem;
                        padding: 0.75rem;
                    }
                    
                    .cookie-text {
                        font-size: 0.8rem;
                        text-align: center;
                    }

                    .cookie-buttons {
                        width: 100%;
                        gap: 0.5rem;
                    }
                    
                    .cookie-btn {
                        flex: 1;
                        padding: 0.5rem;
                        font-size: 0.75rem;
                    }
                }
            `}</style>
            <AuthorityStripe />
            <div className="container" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.75rem 1rem'
            }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Link prefetch={true} href="/" style={{
                        fontSize: '1.4rem',
                        fontWeight: 900,
                        color: 'var(--primary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        whiteSpace: 'nowrap',
                        zIndex: 1001,
                        letterSpacing: '-0.02em',
                        textDecoration: 'none'
                    }}>
                        <Image
                            src="/logo.svg"
                            alt="CorpToolset Logo"
                            width={38}
                            height={38}
                            priority
                            fetchPriority="high"
                            style={{
                                objectFit: 'contain',
                                display: 'block'
                            }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{
                                fontSize: 'clamp(1.1rem, 1rem + 1vw, 1.5rem)',
                                fontWeight: 800,
                                color: 'var(--primary)',
                                letterSpacing: '-0.03em',
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center'
                            }}>CorpToolset</span>
                            <span style={{
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                color: '#64748b',
                                letterSpacing: '0.02em',
                                lineHeight: 1.5,
                                marginTop: '0.1rem',
                                textTransform: 'uppercase',
                                display: 'inline-block'
                            }} className="desktop-only">Private Web Utilities</span>
                        </div>
                    </Link>
                </div>

                {/* Desktop Navigation & Lang Switcher */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '2.5rem' }}>
                    <nav className="desktop-only">
                        <ul style={{
                            display: 'flex',
                            gap: '1.75rem',
                            listStyle: 'none',
                            alignItems: 'center',
                            margin: 0,
                            padding: 0
                        }}>
                            <li>
                                <Link prefetch={true} href="/" style={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    color: pathname === '/' ? 'var(--primary)' : '#475569'
                                }}>
                                    {t('nav_home')}
                                </Link>
                            </li>

                            <li
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                style={{ position: 'relative' }}
                            >
                                <Link
                                    href="/tools"
                                    prefetch={true}
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        color: isToolsOpen || pathname.startsWith('/tools') ? 'var(--primary)' : '#475569',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        textDecoration: 'none'
                                    }}
                                    onMouseEnter={handleMouseEnter}
                                >
                                    {t('all_tools')}
                                    <span style={{
                                        fontSize: '0.6rem',
                                        transition: 'transform 0.3s',
                                        transform: isToolsOpen ? 'rotate(180deg)' : 'none'
                                    }}>▼</span>
                                </Link>

                                {isToolsOpen && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        paddingTop: '1.5rem',
                                        zIndex: 1000
                                    }}>
                                        <div style={{
                                            width: 'min(95vw, 1200px)',
                                            background: '#fff',
                                            boxShadow: '0 20px 50px -12px rgba(0,0,0,0.15)',
                                            borderRadius: '24px',
                                            border: '1px solid var(--border)',
                                            padding: 'clamp(1.5rem, 3vw, 3rem)',
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                            gap: '2rem',
                                            animation: 'fadeInUp 0.2s ease-out'
                                        }}>
                                            {toolCategories.map(cat => (
                                                <div key={cat.name}>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 800,
                                                        color: '#94a3b8',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        marginBottom: '1rem',
                                                        paddingLeft: '0.5rem'
                                                    }}>{cat.name}</div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                        {cat.tools.map(tool => (
                                                            <Link
                                                                key={tool.href}
                                                                href={tool.href}
                                                                style={{
                                                                    padding: '0.6rem 0.75rem',
                                                                    borderRadius: '8px',
                                                                    fontSize: '0.875rem',
                                                                    transition: 'all 0.2s',
                                                                    textDecoration: 'none',
                                                                    color: '#475569',
                                                                    fontWeight: 500,
                                                                    display: 'block'
                                                                }}
                                                                onMouseOver={(e) => {
                                                                    e.currentTarget.style.background = '#f1f5f9';
                                                                    e.currentTarget.style.color = 'var(--primary)';
                                                                }}
                                                                onMouseOut={(e) => {
                                                                    e.currentTarget.style.background = 'transparent';
                                                                    e.currentTarget.style.color = '#475569';
                                                                }}
                                                            >
                                                                {tool.name}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>

                            <li>
                                <Link prefetch={true} href="/blog" style={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    color: pathname.startsWith('/blog') ? 'var(--primary)' : '#475569'
                                }}>
                                    {t('nav_intelligence')}
                                </Link>
                            </li>

                            <li>
                                <Link prefetch={true} href="/about" style={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    color: pathname === '/about' ? 'var(--primary)' : '#475569'
                                }}>
                                    {t('nav_about')}
                                </Link>
                            </li>

                            <li
                                onMouseEnter={() => setIsLegalOpen(true)}
                                onMouseLeave={() => setIsLegalOpen(false)}
                                style={{ position: 'relative' }}
                            >
                                <button
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        color: isLegalOpen ? 'var(--primary)' : '#475569',
                                        background: 'transparent',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {t('legal')}
                                    <span style={{
                                        fontSize: '0.6rem',
                                        transition: 'transform 0.3s',
                                        transform: isLegalOpen ? 'rotate(180deg)' : 'none'
                                    }}>▼</span>
                                </button>

                                {isLegalOpen && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        paddingTop: '1.5rem',
                                        zIndex: 1000
                                    }}>
                                        <div style={{
                                            width: 'min(90vw, 260px)',
                                            background: '#fff',
                                            boxShadow: '0 20px 50px -12px rgba(0,0,0,0.15)',
                                            borderRadius: '16px',
                                            border: '1px solid var(--border)',
                                            padding: '1.25rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.25rem',
                                            animation: 'fadeInUp 0.2s ease-out'
                                        }}>
                                            <Link prefetch={true} href="/privacy" style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', fontSize: '0.875rem', color: '#475569', textDecoration: 'none', fontWeight: 500 }} onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = 'var(--primary)'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; }}>
                                                {t('privacy_policy')}
                                            </Link>
                                            <Link prefetch={true} href="/terms" style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', fontSize: '0.875rem', color: '#475569', textDecoration: 'none', fontWeight: 500 }} onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = 'var(--primary)'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; }}>
                                                {t('terms_service')}
                                            </Link>
                                            <Link prefetch={true} href="/disclaimer" style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', fontSize: '0.875rem', color: '#475569', textDecoration: 'none', fontWeight: 500 }} onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = 'var(--primary)'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; }}>
                                                {t('disclaimer')}
                                            </Link>
                                            <Link prefetch={true} href="/cookies" style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', fontSize: '0.875rem', color: '#475569', textDecoration: 'none', fontWeight: 500 }} onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = 'var(--primary)'; }} onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; }}>
                                                {t('nav_cookies')}
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </li>
                            <li>
                                <Link prefetch={true} href="/contact" style={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    color: pathname === '/contact' ? 'var(--primary)' : '#475569'
                                }}>
                                    {t('nav_contact')}
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: 'min(1.5rem, 3vw)', display: 'flex', alignItems: 'center' }} className="desktop-only">
                        <LanguageSwitcher />
                    </div>

                    {/* Mobile Hamburger Button */}
                    <button
                        onClick={toggleMobileMenu}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '5px',
                            padding: '10px',
                            zIndex: 1001,
                            background: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}
                        className="mobile-only"
                    >
                        <span style={{ width: '22px', height: '2px', background: '#334155', transition: '0.3s', transform: isMobileMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }}></span>
                        <span style={{ width: '22px', height: '2px', background: '#334155', transition: '0.3s', opacity: isMobileMenuOpen ? 0 : 1 }}></span>
                        <span style={{ width: '22px', height: '2px', background: '#334155', transition: '0.3s', transform: isMobileMenuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }}></span>
                    </button>
                </div>

                {/* Mobile Navigation Overlay */}
                {isMobileMenuOpen && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100dvh',
                        background: '#fff',
                        zIndex: 1000,
                        padding: '5rem 1rem 2rem',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <ul style={{
                            listStyle: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            padding: 0
                        }}>
                            <li style={{ padding: '0.5rem 1rem 1rem', borderBottom: '1px solid #f1f5f9', marginBottom: '1rem' }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Select Language</div>
                                <LanguageSwitcher />
                            </li>

                            <li>
                                <Link prefetch={false} href="/" onClick={() => setIsMobileMenuOpen(false)} style={{
                                    fontSize: '1.2rem',
                                    fontWeight: 700,
                                    display: 'block',
                                    padding: '1.25rem',
                                    background: '#f8fafc',
                                    borderRadius: '16px',
                                    color: pathname === '/' ? 'var(--primary)' : '#1e293b'
                                }}>
                                    🏠 {t('nav_home')}
                                </Link>
                            </li>
                            <li>
                                <div style={{
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                    marginBottom: '0.75rem',
                                    marginTop: '1.5rem',
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    paddingLeft: '1rem'
                                }}>
                                    {t('all_tools')}
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1.5rem'
                                }}>
                                    {toolCategories.map(cat => (
                                        <div key={cat.name}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 900, color: 'var(--primary)', marginBottom: '0.5rem', paddingLeft: '1rem' }}>{cat.name}</div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.4rem' }}>
                                                {cat.tools.map(tool => (
                                                    <Link
                                                        key={tool.href}
                                                        href={tool.href}
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                        style={{
                                                            fontSize: '0.95rem',
                                                            color: '#475569',
                                                            padding: '0.85rem 1.25rem',
                                                            background: '#f8fafc',
                                                            borderRadius: '12px',
                                                            fontWeight: 600,
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            textDecoration: 'none',
                                                            border: pathname === tool.href ? '1px solid var(--primary)' : '1px solid transparent'
                                                        }}
                                                    >
                                                        {tool.name}
                                                        <span style={{ color: 'var(--primary)', opacity: 0.5 }}>→</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </li>
                            <li style={{ marginTop: '1rem' }}>
                                <Link prefetch={false} href="/blog" onClick={() => setIsMobileMenuOpen(false)} style={{
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    display: 'block',
                                    padding: '1.25rem',
                                    borderRadius: '16px',
                                    background: '#f8fafc',
                                    color: pathname.startsWith('/blog') ? 'var(--primary)' : '#1e293b'
                                }}>
                                    📰 {t('nav_intelligence')}
                                </Link>
                            </li>
                            <li>
                                <Link prefetch={false} href="/about" onClick={() => setIsMobileMenuOpen(false)} style={{
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    display: 'block',
                                    padding: '1.25rem',
                                    borderRadius: '16px',
                                    background: '#f8fafc',
                                    color: pathname === '/about' ? 'var(--primary)' : '#1e293b'
                                }}>
                                    💡 {t('nav_about')}
                                </Link>
                            </li>
                            <li>
                                <Link prefetch={false} href="/contact" onClick={() => setIsMobileMenuOpen(false)} style={{
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    display: 'block',
                                    padding: '1.25rem',
                                    borderRadius: '16px',
                                    background: '#f8fafc',
                                    color: pathname === '/contact' ? 'var(--primary)' : '#1e293b'
                                }}>
                                    📞 {t('nav_contact')}
                                </Link>
                            </li>
                            <li style={{ marginTop: '2rem' }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '1rem', paddingLeft: '1rem' }}>{t('legal')}</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                    <Link prefetch={false} href="/privacy" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{t('privacy_policy')}</Link>
                                    <Link prefetch={false} href="/terms" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{t('terms_service')}</Link>
                                    <Link prefetch={false} href="/disclaimer" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#334155', gridColumn: 'span 2' }}>{t('disclaimer')}</Link>
                                </div>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </header>
    );
}





